import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot,
  Lock,
  Unlock,
  CheckCircle2,
  ExternalLink,
  ArrowDownToLine,
  Loader2,
  Sparkles,
  Clock,
  Zap,
  ShieldCheck,
  Cpu,
} from 'lucide-react';
import type { TaskDto } from '@nova-mine/shared';
import { formatCoinAmount } from '@nova-mine/shared';
import { MiningReactor } from '../components/common/MiningReactor.js';
import { AnimatedCoinCounter } from '../components/common/AnimatedCoinCounter.js';
import { GlassCard } from '../components/common/GlassCard.js';
import { AnimatedButton } from '../components/common/AnimatedButton.js';
import { MinerStatusBadge } from '../components/common/MinerStatusBadge.js';
import { sound } from '../utils/sound.js';
import { api } from '../services/api.js';

interface AutoMinerScreenProps {
  autoUnlocked: boolean;
  autoAccrued: number;
  lastClaimedAtMs: number;
  tasks: TaskDto[];
  isClaiming: boolean;
  claimSuccessMessage: string | null;
  onClaim: () => Promise<any>;
  onTriggerFloatingCoins: () => void;
  onRefreshTasks: () => void;
  accountBalance?: number;
  onSwitchToMain?: () => void;
}

export const AutoMinerScreen: React.FC<AutoMinerScreenProps> = ({
  autoUnlocked,
  autoAccrued,
  lastClaimedAtMs,
  tasks,
  isClaiming,
  claimSuccessMessage,
  onClaim,
  onTriggerFloatingCoins,
  onRefreshTasks,
  accountBalance = 0,
  onSwitchToMain,
}) => {
  const [verifyingId, setVerifyingId] = useState<string | number | null>(null);
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);
  const [taskStatusMsg, setTaskStatusMsg] = useState<string | null>(null);
  const [progressPercent, setProgressPercent] = useState(0);
  const [elapsedString, setElapsedString] = useState('00h 00m');

  const maxBufferSeconds = 86400;

  useEffect(() => {
    if (!autoUnlocked) return;

    const update = () => {
      const now = Date.now();
      const elapsedMs = Math.max(0, now - lastClaimedAtMs);
      const elapsedSec = Math.min(maxBufferSeconds, elapsedMs / 1000);

      const percent = (elapsedSec / maxBufferSeconds) * 100;
      setProgressPercent(percent);

      const hours = Math.floor(elapsedSec / 3600);
      const mins = Math.floor((elapsedSec % 3600) / 60);
      setElapsedString(`${String(hours).padStart(2, '0')}h ${String(mins).padStart(2, '0')}m`);
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [autoUnlocked, lastClaimedAtMs]);

  // Filter for the 3 website view tasks
  const websiteTasks = tasks.filter((t) => t.task_type === 'website_view').slice(0, 3);
  const completedCount = websiteTasks.filter((t) => t.status === 'completed').length;
  const canClaim = autoAccrued >= 0.0001;

  const handleVerifyWebsiteTask = async (task: TaskDto) => {
    if (task.status === 'completed' || verifyingId !== null) return;
    try {
      setVerifyingId(task.id);
      setTaskStatusMsg('Opening partner destination...');
      sound.playTap();

      // 1. Start server attempt
      const attemptData = await api.startTask(String(task.id));

      // 2. Open website visibly in new window
      if (attemptData.url) {
        window.open(attemptData.url, '_blank');
      }

      // 3. Countdown dwell time (min_dwell_seconds, default 8s)
      const dwell = attemptData.min_dwell_seconds || 8;
      for (let s = dwell; s > 0; s--) {
        setCountdownSeconds(s);
        setTaskStatusMsg(`Dwell active: ${s}s remaining on external site...`);
        await new Promise((r) => setTimeout(r, 1000));
      }

      setCountdownSeconds(null);
      setTaskStatusMsg('Validating engagement on server...');

      // 4. Submit verification with attempt_id
      const res = await api.verifyTask(attemptData.attempt_id);

      if (res.auto_miner_unlocked) {
        sound.playUnlockSurge();
        setTaskStatusMsg('Quest complete! Autonomous Mining Rig unlocked!');
      } else {
        sound.playCoinChime();
        setTaskStatusMsg(`+${res.reward_coins} NOVA credited! Complete remaining tasks.`);
      }

      onRefreshTasks();
      setTimeout(() => setTaskStatusMsg(null), 3500);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Verification failed';
      setTaskStatusMsg(msg);
      alert(msg);
      setTimeout(() => setTaskStatusMsg(null), 4000);
    } finally {
      setVerifyingId(null);
      setCountdownSeconds(null);
    }
  };

  const handleClaim = async () => {
    if (!canClaim || isClaiming) return;
    try {
      await onClaim();
    } catch {
      // Handled in parent
    }
  };

  return (
    <div className="w-full space-y-5 animate-fade-in pb-4">
      {/* 1. Mining Fleet Sub-Navigation Switcher */}
      {onSwitchToMain && (
        <div className="flex items-center p-1 rounded-2xl glass-panel-deep border border-white/10 max-w-xs mx-auto">
          <button
            onClick={onSwitchToMain}
            className="flex-1 py-1.5 px-3 rounded-xl text-xs font-bold transition-all text-gray-400 hover:text-white flex items-center justify-center space-x-1.5"
          >
            <Cpu className="w-3.5 h-3.5" />
            <span>Main Core</span>
          </button>
          <button
            className="flex-1 py-1.5 px-3 rounded-xl text-xs font-bold transition-all bg-gradient-to-r from-cyan-500/30 to-blue-500/30 text-cyan-300 border border-cyan-500/40 shadow-sm flex items-center justify-center space-x-1.5"
          >
            <Bot className="w-3.5 h-3.5 text-nova-cyan" />
            <span>Autonomous</span>
          </button>
        </div>
      )}

      {/* 2. Header Quest & Unlock Status Card */}
      <GlassCard
        variant={autoUnlocked ? 'cyan' : 'deep'}
        className="border-cyan-500/35"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3.5">
            <div
              className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg ${
                autoUnlocked
                  ? 'bg-cyan-500/20 border border-cyan-400 text-cyan-300 shadow-cyan-500/25'
                  : 'bg-space-850 border border-white/10 text-gray-400'
              }`}
            >
              <Bot className="w-6 h-6 text-nova-cyan" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="font-black text-white text-base">Autonomous Mining Rig</h3>
                {autoUnlocked ? (
                  <MinerStatusBadge status="unlocked" />
                ) : (
                  <MinerStatusBadge status="locked" />
                )}
              </div>
              <p className="text-xs text-cyan-200/80 mt-0.5">
                Yield: <span className="text-white font-mono font-bold">2,000 NOVA</span> per 24 hours
              </p>
            </div>
          </div>
        </div>

        {/* Quest Unlock Progress Bar */}
        <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-400">Unlock Quest Requirement:</span>
            <span className="font-mono font-bold text-cyan-300">
              {completedCount} / 3 Tasks Verified
            </span>
          </div>

          <div className="w-full bg-black/40 h-2.5 rounded-full overflow-hidden border border-white/10">
            <div
              className="bg-gradient-to-r from-cyan-500 via-teal-400 to-blue-500 h-full rounded-full transition-all duration-500 shadow-[0_0_10px_#00f2fe]"
              style={{ width: `${(completedCount / 3) * 100}%` }}
            />
          </div>
        </div>
      </GlassCard>

      {/* 3. Unlocked Active Reactor State */}
      {autoUnlocked && (
        <GlassCard variant="cyan" className="flex flex-col items-center text-center p-6 relative border-cyan-500/40">
          <AnimatePresence>
            {claimSuccessMessage && (
              <motion.div
                initial={{ opacity: 0, y: -20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.9 }}
                className="absolute top-4 z-20 flex items-center space-x-2 bg-emerald-500/90 text-white font-black text-xs px-4 py-2 rounded-full shadow-lg backdrop-blur-md border border-emerald-400/30"
              >
                <CheckCircle2 className="w-4 h-4 text-white" />
                <span>{claimSuccessMessage}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Header Tag */}
          <div className="w-full flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
              <span className="text-[11px] font-black uppercase tracking-widest text-cyan-200">
                Quantum Auto Rig • Autonomous Hash
              </span>
            </div>

            <MinerStatusBadge status="active" label="Continuous" />
          </div>

          {/* Central Circular Progress Reactor */}
          <div className="my-6 flex items-center justify-center">
            <MiningReactor
              size={220}
              progressPercent={progressPercent}
              ratePerSecond={2000 / 86400}
              status={isClaiming ? 'claiming' : 'mining'}
              variant="cyan"
            />
          </div>

          {/* Accrued Ticker Display */}
          <div className="w-full max-w-sm bg-black/35 rounded-2xl p-4 border border-white/10 backdrop-blur-md">
            <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-200/80 block">
              Auto Accrued Unclaimed Coins
            </span>

            <div className="mt-1 flex items-baseline justify-center space-x-2">
              <AnimatedCoinCounter
                value={autoAccrued}
                ratePerSecond={2000 / 86400}
                decimals={4}
                className="text-3xl sm:text-4xl font-black font-mono text-cyan-gradient tracking-tight"
              />
              <span className="text-sm font-black text-cyan-300 font-mono">NOVA</span>
            </div>

            <div className="mt-2.5 pt-2 border-t border-white/10 flex items-center justify-between text-xs text-gray-400">
              <span className="flex items-center space-x-1">
                <Clock className="w-3.5 h-3.5 text-cyan-400" />
                <span>Elapsed: <strong className="text-white font-mono">{elapsedString}</strong></span>
              </span>
              <span className="font-mono text-cyan-300 font-bold">
                +{(2000 / 86400).toFixed(4)}/s
              </span>
            </div>
          </div>

          {/* Claim Button */}
          <div className="w-full max-w-sm mt-5">
            <AnimatedButton
              variant="cyan"
              size="lg"
              onClick={handleClaim}
              disabled={!canClaim || isClaiming}
              isLoading={isClaiming}
              icon={<ArrowDownToLine className="w-5 h-5" />}
              className="w-full justify-center shadow-cyan-500/25"
            >
              Claim Auto Miner Coins
            </AnimatedButton>
          </div>
        </GlassCard>
      )}

      {/* 4. The 3 Required Website-View Tasks */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <h4 className="text-xs font-black uppercase tracking-wider text-gray-300">
            {autoUnlocked ? 'Quest Verification Credentials' : '3 Required Website Quests'}
          </h4>
          <span className="text-[11px] font-mono text-cyan-400 font-bold">
            {completedCount} / 3 Completed
          </span>
        </div>

        {/* Live Task Status Banner */}
        {taskStatusMsg && (
          <div className="text-xs px-3.5 py-2.5 rounded-2xl border bg-cyan-950/40 border-cyan-500/30 text-cyan-300 flex items-center space-x-2 animate-fade-in shadow-md">
            {countdownSeconds !== null ? (
              <Loader2 className="w-4 h-4 animate-spin flex-shrink-0 text-cyan-400" />
            ) : (
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-cyan-400" />
            )}
            <span>{taskStatusMsg}</span>
          </div>
        )}

        {websiteTasks.map((t, index) => {
          const isDone = t.status === 'completed';
          const isBusy = verifyingId === t.id;

          return (
            <GlassCard
              key={t.id}
              variant={isDone ? 'deep' : isBusy ? 'cyan' : 'default'}
              className="p-4"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center space-x-3 min-w-0">
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                      isDone
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : isBusy
                        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400'
                        : 'bg-space-850 text-cyan-400 border border-cyan-500/30'
                    }`}
                  >
                    {isDone ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : isBusy && countdownSeconds !== null ? (
                      <span className="font-mono text-[11px]">{countdownSeconds}s</span>
                    ) : (
                      `0${index + 1}`
                    )}
                  </div>
                  <div className="min-w-0">
                    <h5 className="font-bold text-white text-xs leading-snug truncate">{t.title}</h5>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-[11px] font-mono text-nova-gold font-bold">
                        +{t.reward_coins} NOVA
                      </span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 font-semibold">
                        Quest {index + 1} of 3
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex-shrink-0">
                  {isDone ? (
                    <span className="px-3 py-1.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-bold flex items-center space-x-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>Done</span>
                    </span>
                  ) : (
                    <AnimatedButton
                      variant="cyan"
                      size="sm"
                      onClick={() => handleVerifyWebsiteTask(t)}
                      disabled={isBusy || verifyingId !== null}
                      isLoading={isBusy && countdownSeconds === null}
                      icon={isBusy ? undefined : <ExternalLink className="w-3.5 h-3.5" />}
                    >
                      {isBusy && countdownSeconds !== null ? `${countdownSeconds}s Dwell` : 'Visit & Verify'}
                    </AnimatedButton>
                  )}
                </div>
              </div>
            </GlassCard>
          );
        })}

        {/* Verification Limitations & Policy Notice */}
        <div className="rounded-2xl bg-black/30 border border-white/5 p-4 flex items-start space-x-2.5 text-[11px] text-gray-400">
          <ShieldCheck className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
          <span>
            Quest Policy: Each visit task monitors elapsed dwell time and verifies non-replay engagement on the server before unlocking the autonomous yield core.
          </span>
        </div>
      </div>
    </div>
  );
};
